import { WithPopover } from "@/components/environments/WithPopover";
import { Button } from "@/components/flexkit/Button";
import { Input } from "@/components/podkit/forms/Input";
import { Label } from "@/components/podkit/forms/Label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/podkit/select/Select";
import { useToast } from "@/components/podkit/toasts/use-toast";
import { Heading2 } from "@/components/podkit/typography/Headings";
import { Text } from "@/components/podkit/typography/Text";
import { TrackLocations } from "@/hooks/use-segment";
import { useUpdateEnvironment } from "@/queries/environment-queries";
import { formatError } from "@/utils/errors";
import {
    AdmissionLevel,
    EnvironmentSpec,
    EnvironmentSpec_EnvironmentPort,
    EnvironmentSpec_EnvironmentPort_Protocol,
    UpdateEnvironmentRequest,
} from "gitpod-next-api/gitpod/v1/environment_pb";
import { useCallback, useId, useState, type FC, type FormEvent, type ReactElement } from "react";

export type PortFormData = {
    name: string;
    port: number;
    protocol: EnvironmentSpec_EnvironmentPort_Protocol;
};

type PortFormProps = {
    environmentId: string;
    onClose?: () => void;
    initialData?: PortFormData;
    mode?: "create" | "edit";
};

const PortForm: FC<PortFormProps> = ({ environmentId, onClose, initialData, mode = "create" }) => {
    const updateEnvironment = useUpdateEnvironment();
    const { toast } = useToast();

    const [name, setName] = useState<string>(initialData?.name ?? "");
    const [port, setPort] = useState<string>(initialData?.port?.toString() ?? "");
    const [protocol, setProtocol] = useState<EnvironmentSpec_EnvironmentPort_Protocol>(
        initialData?.protocol ?? EnvironmentSpec_EnvironmentPort_Protocol.HTTP,
    );
    const inputNameID = useId();
    const inputPortID = useId();
    const selectProtocolID = useId();

    const isEditMode = mode === "edit";

    const handleSubmit = useCallback(
        async (event: FormEvent<HTMLFormElement>) => {
            event.preventDefault();
            if (!port || !environmentId) {
                return;
            }

            try {
                await updateEnvironment.mutateAsync({
                    req: new UpdateEnvironmentRequest({
                        environmentId,
                        spec: new EnvironmentSpec({
                            ports: [
                                new EnvironmentSpec_EnvironmentPort({
                                    name: name || "",
                                    port: Number(port),
                                    admission: AdmissionLevel.EVERYONE,
                                    protocol: protocol,
                                }),
                            ],
                        }),
                    }),
                });

                toast({
                    title: isEditMode ? "Port updated" : "Port opened",
                });

                // close on success only
                onClose?.();
            } catch (error) {
                toast({
                    title: isEditMode ? "Failed to update port." : "Failed to open port.",
                    description: formatError(error),
                });
            }
        },
        [port, environmentId, updateEnvironment, name, protocol, onClose, toast, isEditMode],
    );

    return (
        <form onSubmit={handleSubmit} className="p-5">
            <div className="flex w-[500px] flex-col gap-7">
                <div>
                    <Heading2>{isEditMode ? "Edit port" : "Open a public port"}</Heading2>
                    <Text muted className="text-base">
                        {isEditMode
                            ? "Update the port name or protocol"
                            : "Public ports can be shared with other people"}
                    </Text>
                </div>
                <div className="flex flex-col gap-2">
                    <div className="space-y-1">
                        <Label htmlFor={inputNameID}>Name</Label>
                        <Input
                            id={inputNameID}
                            type="text"
                            name="name"
                            value={name || ""}
                            onChange={(e) => setName(e.target.value)}
                        />
                    </div>
                    <div className="space-y-1">
                        <Label htmlFor={inputPortID}>Port</Label>
                        <Input
                            id={inputPortID}
                            type="number"
                            name="port"
                            value={port || ""}
                            onChange={(e) => setPort(e.target.value)}
                            disabled={isEditMode}
                        />
                        {isEditMode && (
                            <Text muted className="text-sm">
                                Port number cannot be changed
                            </Text>
                        )}
                    </div>
                    <div className="space-y-1">
                        <Label htmlFor={selectProtocolID}>Protocol</Label>
                        <Select
                            value={protocol.toString()}
                            onValueChange={(value) =>
                                setProtocol(Number(value) as EnvironmentSpec_EnvironmentPort_Protocol)
                            }
                        >
                            <SelectTrigger id={selectProtocolID} loading={false}>
                                <SelectValue placeholder="Select protocol" />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem
                                    key={EnvironmentSpec_EnvironmentPort_Protocol.HTTP}
                                    value={EnvironmentSpec_EnvironmentPort_Protocol.HTTP.toString()}
                                >
                                    <div>HTTP</div>
                                </SelectItem>
                                <SelectItem
                                    key={EnvironmentSpec_EnvironmentPort_Protocol.HTTPS}
                                    value={EnvironmentSpec_EnvironmentPort_Protocol.HTTPS.toString()}
                                >
                                    <div>HTTPS</div>
                                </SelectItem>
                            </SelectContent>
                        </Select>
                        <Text muted className="text-sm">
                            Choose HTTPS if your service requires SSL/TLS connections
                        </Text>
                    </div>
                </div>
                <div className="flex flex-row justify-end gap-2">
                    <Button
                        type="button"
                        variant="secondary"
                        onClick={onClose}
                        data-tracking-id={isEditMode ? "cancel-edit-port-form" : "cancel-open-port-form"}
                    >
                        Cancel
                    </Button>
                    <Button
                        loading={updateEnvironment.isPending}
                        autoFocus={true}
                        aria-label={isEditMode ? "Save Changes" : "Open Port"}
                        type="submit"
                        disabled={!port}
                        variant="primary"
                        data-tracking-id={isEditMode ? "save-edit-port-form" : "open-port-open-port-form"}
                    >
                        {isEditMode ? "Save" : "Open Port"}
                    </Button>
                </div>
            </div>
        </form>
    );
};

type OpenPortFormProps = {
    environmentId: string;
    onClose?: () => void;
};
const OpenPortForm: FC<OpenPortFormProps> = ({ environmentId, onClose }) => {
    return <PortForm environmentId={environmentId} onClose={onClose} mode="create" />;
};

export type OpenPortPopoverProps = {
    button: ReactElement;
    anchor?: React.RefObject<HTMLElement>;
    environmentId: string;
};
export const OpenPortPopover: FC<OpenPortPopoverProps> = ({ anchor, button, environmentId }) => {
    return (
        <WithPopover
            anchor={anchor}
            button={button}
            align="end"
            data-track-location={TrackLocations.EnvironmentOpenPortPoppover}
        >
            <OpenPortForm environmentId={environmentId} />
        </WithPopover>
    );
};

export type EditPortPopoverProps = {
    button: ReactElement;
    anchor?: React.RefObject<HTMLElement>;
    environmentId: string;
    portData: PortFormData;
    buttonTooltip?: string;
};
export const EditPortPopover: FC<EditPortPopoverProps> = ({ anchor, button, environmentId, portData, buttonTooltip }) => {
    return (
        <WithPopover
            anchor={anchor}
            button={button}
            align="end"
            data-track-location={TrackLocations.EnvironmentEditPortPopover}
            buttonTooltip={buttonTooltip}
        >
            <PortForm environmentId={environmentId} initialData={portData} mode="edit" />
        </WithPopover>
    );
};
